/**
 * Plugin marketplace admin UI — lists the registry, publishes, edits, deletes.
 *
 * Storage layout plugin-host expects:
 *   /plugins/registry.json
 *   /plugins/<id>/<version>/{manifest.json, app-cfg.json, index.js, screenshot.png}
 *
 * No auth in v1 (site-wide basic auth sits above). Validation is two layers:
 * `validateManifest` checks shape, `checkStoreRules` checks what only the store
 * knows.
 */
import {
  type PluginKind,
  type PluginType,
  type PluginManifest,
  type PluginRegistry,
  type PluginRegistryEntry,
  PLUGIN_TYPES,
  validateManifest,
} from '@ais/plugin-host';
import './styles.css';
import { openLightbox } from './ui/lightbox';
import { toast } from './ui/toast';
import { confirmModal, openDetailModal, type DetailModalHandle } from './ui/modal';
import { renderSkeletonTiles } from './ui/skeleton';
import { siteRoot, editorUrl, parseEditorOverride, resolveStoreUrl } from './site-base';

// --- Constants ---------------------------------------------------------------

// Everything the store addresses that is not its own asset hangs off SITE, so a
// site build reaches its own registry/editor. Unset VIEWER_BASE → '/' (see
// site-base.ts).
declare const __SERVER_BASE__: string;
const SITE = siteRoot(__SERVER_BASE__, import.meta.env.BASE_URL, window.location.href);
const REGISTRY_URL = `${SITE}plugins/registry.json`;
const PLUGINS_BASE = `${SITE}plugins`;
// "Open in editor" target. ?editor= overrides per visit, so one built store can
// review against any editor deployment.
const EDITOR_PARAM = new URLSearchParams(location.search).get('editor');
const EDITOR_OVERRIDE = parseEditorOverride(EDITOR_PARAM);
if (EDITOR_PARAM && !EDITOR_OVERRIDE) {
  console.warn('[marketplace] ignoring ?editor= — expected a path or an http(s) URL');
}
const EDITOR_BASE = EDITOR_OVERRIDE ?? `${SITE}admin/`;

// --- DOM lookups -------------------------------------------------------------

const $list = document.getElementById('mp-list') as HTMLDivElement;
$list.classList.add('tile-grid');
const $refresh = document.getElementById('mp-refresh') as HTMLButtonElement;
const $filter = document.getElementById('mp-filter') as HTMLInputElement | null;
const $count = document.getElementById('mp-count') as HTMLDivElement | null;
const $tabViewer = document.getElementById('mp-tab-viewer') as HTMLButtonElement | null;
const $tabWidget = document.getElementById('mp-tab-widget') as HTMLButtonElement | null;
const $form = document.getElementById('mp-upload-form') as HTMLFormElement;
const $kindSelect = document.getElementById('mp-kind') as HTMLSelectElement | null;
const $manifestInput = document.getElementById('mp-manifest') as HTMLInputElement;
const $bundleInput = document.getElementById('mp-bundle') as HTMLInputElement;
const $screenshotInput = document.getElementById('mp-screenshot') as HTMLInputElement;
const $manifestPreview = document.getElementById('mp-manifest-preview') as HTMLDivElement;
const $publishBtn = document.getElementById('mp-publish') as HTMLButtonElement;

// --- State -------------------------------------------------------------------

interface ListedPlugin {
  entry: PluginRegistryEntry;
  manifest: PluginManifest | null;   // null if manifest fetch failed
  manifestError?: string;
}

let pendingManifest: PluginManifest | null = null;

// Which kind tab the list is currently filtered to. Missing `kind` on a
// registry entry is treated as 'viewer-plugin' for back-compat with v1 entries.
let currentKindTab: 'viewer-plugin' | 'dashboard-widget' = 'viewer-plugin';

function entryKind(entry: PluginRegistryEntry): PluginKind {
  return entry.kind ?? 'viewer-plugin';
}

// Last render's items. Held so the filter input can re-render without
// re-fetching the registry. Always re-populated on loadAndRender(); never
// stale — every mutating path calls loadAndRender() after writing.
let lastItems: ListedPlugin[] = [];

// --- Registry I/O ------------------------------------------------------------

async function fetchRegistry(): Promise<PluginRegistry> {
  // Always force a fresh read; the marketplace mutates this file and we want
  // to see other tabs' writes too.
  const r = await fetch(REGISTRY_URL, { cache: 'no-store' });
  if (r.status === 404) return { plugins: [] };
  if (!r.ok) throw new Error(`registry fetch HTTP ${r.status}`);
  const json = (await r.json()) as PluginRegistry;
  if (!json || !Array.isArray(json.plugins)) {
    throw new Error('registry.json malformed');
  }
  return json;
}

async function writeRegistry(reg: PluginRegistry): Promise<void> {
  const r = await fetch(REGISTRY_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reg, null, 2),
  });
  if (!r.ok) throw new Error(`registry PUT failed: HTTP ${r.status}`);
}

async function fetchManifest(url: string): Promise<PluginManifest> {
  const r = await fetch(url, { cache: 'no-store' });
  if (!r.ok) throw new Error(`manifest fetch HTTP ${r.status}`);
  return validateManifest(await r.json());
}

// --- Store rules -------------------------------------------------------------
// Meaning, not shape (validateManifest does shape). Run before any PUT so a
// rejected publish leaves nothing half-written.

/** Built-in sidebar section keys (viewer-ui). */
const BUILTIN_SECTIONS = ['pc', 'bim', 'floorplan', 'panos', 'splats', 'map', 'sun'];

/** Prereleases are the draft slot — the only versions the store may overwrite.
 *  semver's rule: anything after the first '-'. */
function isPrerelease(version: string): boolean {
  return version.includes('-');
}

/** Name the offending value — the author is still on the form. */
function checkStoreRules(m: PluginManifest, existing: PluginRegistryEntry | undefined): void {
  const sections = m.ui?.sidebar?.sections;
  if (sections !== undefined) {
    if (!Array.isArray(sections)) throw new Error('ui.sidebar.sections must be an array of section keys');
    for (const k of sections) {
      if (typeof k !== 'string' || !k) throw new Error('ui.sidebar.sections contains a non-string key');
      // Built-ins or its own id. Another plugin's panel exists only if that
      // plugin is installed — that is what dependsOn says.
      if (!BUILTIN_SECTIONS.includes(k) && k !== m.id) {
        throw new Error(
          `ui.sidebar.sections: "${k}" is not a section this plugin can ask for. `
          + `Built-ins are ${BUILTIN_SECTIONS.join(', ')}. For a panel another plugin `
          + 'contributes, declare that plugin in dependsOn — its panel appears when it is installed.',
        );
      }
    }
  }
  if (m.type !== undefined && !PLUGIN_TYPES.includes(m.type)) {
    throw new Error(`type must be one of ${PLUGIN_TYPES.map((t) => `"${t}"`).join(', ')} (got "${String(m.type)}")`);
  }
  // `type` names the shelf, `kind` the host contract; a widget's shelf is the
  // dashboard and nothing else's is, so the two are held in step here rather
  // than left to disagree.
  const widget = (m.kind ?? 'viewer-plugin') === 'dashboard-widget';
  if (widget && m.type !== undefined && m.type !== 'dashboard') {
    throw new Error(`a dashboard widget is type "dashboard" (got "${m.type}") — "app" and "tool" are viewer-plugin types`);
  }
  if (!widget && m.type === 'dashboard') {
    throw new Error('type "dashboard" belongs to kind "dashboard-widget" — a viewer plugin is an "app" or a "tool"');
  }
  if (existing && existing.version === m.version && !isPrerelease(m.version)) {
    throw new Error(
      `${m.id} v${m.version} is already published and a released version is immutable. `
      + 'Bump the version, or use a prerelease (e.g. ' + m.version + '-dev.1) while iterating.',
    );
  }
}

/** A widget's type is derived, not chosen: every `dashboard-widget` the store
 *  writes carries `type: 'dashboard'`, so the index is filterable by shelf
 *  without an author having to know the rule. Viewer plugins are left as
 *  declared — `app`/`tool` is a judgement, and absent means unclassified. */
function stampType(m: PluginManifest): void {
  if ((m.kind ?? 'viewer-plugin') === 'dashboard-widget' && m.type === undefined) m.type = 'dashboard';
}

/** The cfg the store hands out for this version — one translation every host
 *  fetches, authoritative once edited here. The plugin's own panel is NOT added:
 *  since ui 0.32 the allow-list governs built-ins only, so it shows anyway. */
function deriveAppCfg(m: PluginManifest): string {
  return JSON.stringify({
    version: 1,           // AppConfig schema version, not the plugin's
    kind: 'app-cfg',
    label: `${m.id} v${m.version}`,
    ui: m.ui ?? {},
  }, null, 2);
}

async function putFile(url: string, body: BodyInit, contentType: string): Promise<void> {
  const r = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body,
  });
  if (!r.ok) throw new Error(`PUT ${url} → HTTP ${r.status}`);
}

async function deleteFile(url: string): Promise<void> {
  const r = await fetch(url, { method: 'DELETE' });
  // 404 = already gone; treat as success to keep delete idempotent.
  if (!r.ok && r.status !== 404) throw new Error(`DELETE ${url} → HTTP ${r.status}`);
}

// --- List view ---------------------------------------------------------------

async function loadAndRender(): Promise<void> {
  renderSkeletonTiles($list, 8);
  let reg: PluginRegistry;
  try {
    reg = await fetchRegistry();
  } catch (e) {
    lastItems = [];
    updateCount();
    renderRegistryError((e as Error).message);
    return;
  }
  if (reg.plugins.length === 0) {
    lastItems = [];
    updateCount();
    renderEmpty();
    return;
  }
  // Fetch manifests in parallel; keep the registry's ordering.
  const items: ListedPlugin[] = await Promise.all(
    reg.plugins.map(async (entry) => {
      try {
        const manifest = await fetchManifest(absoluteUrl(entry.manifestUrl));
        return { entry, manifest };
      } catch (e) {
        return { entry, manifest: null, manifestError: (e as Error).message };
      }
    }),
  );
  lastItems = items;
  applyFilterAndRender();
}

function applyFilterAndRender(): void {
  // Kind tab first — that's the primary "what am I looking at" filter; the
  // search box narrows within the current tab.
  const kindMatches = lastItems.filter((it) => entryKind(it.entry) === currentKindTab);

  const q = ($filter?.value ?? '').trim().toLowerCase();
  const matches = q
    ? kindMatches.filter((it) => matchesFilter(it, q))
    : kindMatches;

  updateCount(matches.length, kindMatches.length);

  if (kindMatches.length === 0) {
    $list.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'state-card empty-state full-grid';
    const msg = currentKindTab === 'dashboard-widget'
      ? 'No dashboard widgets published yet'
      : 'No viewer plugins published yet';
    box.innerHTML = `
      <div class="state-title">${msg}</div>
      <div class="state-body">Publish a bundle below to populate this tab.</div>
    `;
    $list.appendChild(box);
    return;
  }
  if (kindMatches.length > 0 && matches.length === 0) {
    $list.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'state-card empty-state full-grid';
    box.innerHTML = `
      <div class="state-title">No matches</div>
      <div class="state-body">No plugin matches <code>${escapeHtml(q)}</code>. Try a different term.</div>
    `;
    $list.appendChild(box);
    return;
  }

  $list.innerHTML = '';
  for (const item of matches) $list.appendChild(renderTile(item, matches));
}

function matchesFilter(it: ListedPlugin, q: string): boolean {
  const fields = [
    it.entry.id,
    it.entry.label,
    it.entry.author,
    it.entry.description,
    it.manifest?.label,
    it.manifest?.author,
    it.manifest?.description,
  ];
  return fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(q));
}

function updateCount(shown?: number, tabTotal?: number): void {
  if (!$count) return;
  // The displayed count tracks the active kind tab; the search box narrows
  // within that. tabTotal defaults to the global lastItems length for callers
  // that haven't moved to the tab-aware shape yet.
  const total = tabTotal ?? lastItems.length;
  const noun = currentKindTab === 'dashboard-widget' ? 'widget' : 'plugin';
  if (total === 0) {
    $count.textContent = '';
    return;
  }
  if (shown === undefined || shown === total) {
    $count.textContent = `${total} ${total === 1 ? noun : noun + 's'}`;
  } else {
    $count.textContent = `Showing ${shown} of ${total} ${noun}s`;
  }
}

function renderRegistryError(msg: string): void {
  $list.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'state-card error full-grid';
  box.innerHTML = `
    <div class="state-icon" aria-hidden="true">!</div>
    <div class="state-title">Couldn't load the registry</div>
    <div class="state-body">${escapeHtml(msg)}</div>
    <div class="state-hint">Expected file: <code>/plugins/registry.json</code></div>
  `;
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'primary-btn';
  retry.textContent = 'Retry';
  retry.onclick = () => void loadAndRender();
  box.appendChild(retry);
  $list.appendChild(box);
}

function renderEmpty(): void {
  $list.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'state-card empty-state full-grid';
  // Inline SVG: an outlined package/box mark.
  box.innerHTML = `
    <svg class="state-icon-svg" aria-hidden="true" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M24 6 L42 15 L24 24 L6 15 Z" />
      <path d="M6 15 V33 L24 42 L42 33 V15" />
      <path d="M24 24 V42" />
    </svg>
    <div class="state-title">No plugins published yet</div>
    <div class="state-body">Publish a plugin bundle below to populate the marketplace.</div>
  `;
  const publishCta = document.createElement('button');
  publishCta.type = 'button';
  publishCta.className = 'primary-btn';
  publishCta.textContent = 'Publish your first plugin';
  publishCta.onclick = () => {
    const details = document.getElementById('mp-form-disclosure') as HTMLDetailsElement | null;
    if (details) {
      details.open = true;
      details.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  box.appendChild(publishCta);
  $list.appendChild(box);
}

/** First screenshot, resolved against its own manifest. */
function screenshotUrl(item: ListedPlugin): string | null {
  const shot = item.manifest?.screenshots?.[0];
  return shot ? absoluteUrl(shot, item.entry.manifestUrl) : null;
}

/** A storefront tile. The whole card opens the detail modal — no edit/delete
 *  affordances here. */
function renderTile(item: ListedPlugin, filtered: ListedPlugin[]): HTMLElement {
  const tile = document.createElement('article');
  tile.className = 'tile';
  tile.dataset.id = item.entry.id;
  tile.tabIndex = 0;
  tile.setAttribute('role', 'button');
  const aria = [item.entry.label || item.entry.id,
                `v${item.entry.version}`,
                item.entry.author ? `by ${item.entry.author}` : ''].filter(Boolean).join(', ');
  tile.setAttribute('aria-label', `${aria} — open details`);
  if (item.manifestError) tile.classList.add('error');

  const open = (): void => openPluginDetail(item, filtered);
  tile.addEventListener('click', open);
  tile.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  });

  // ---- Media (top): screenshot or gradient placeholder. ----
  const media = document.createElement('div');
  media.className = 'tile-media';
  const shotUrl = screenshotUrl(item);
  if (shotUrl) {
    const img = document.createElement('img');
    img.src = shotUrl;
    img.alt = `${item.manifest?.label ?? item.entry.label ?? item.entry.id} screenshot`;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.onerror = () => {
      img.remove();
      media.appendChild(buildTilePlaceholder(item));
    };
    media.appendChild(img);
  } else {
    media.appendChild(buildTilePlaceholder(item));
  }
  tile.appendChild(media);

  // ---- Body: title row, author, description, chips. ----
  const body = document.createElement('div');
  body.className = 'tile-body';

  const titleRow = document.createElement('div');
  titleRow.className = 'tile-title-row';
  const label = document.createElement('h3');
  label.className = 'tile-label';
  label.textContent = item.entry.label || item.entry.id;
  const ver = document.createElement('span');
  ver.className = 'tile-version mono';
  ver.textContent = `v${item.entry.version}`;
  titleRow.append(label, ver);
  body.appendChild(titleRow);

  const author = item.entry.author ?? item.manifest?.author;
  if (author) {
    const a = document.createElement('p');
    a.className = 'tile-author';
    a.textContent = `by ${author}`;
    body.appendChild(a);
  }

  const desc = item.entry.description ?? item.manifest?.description;
  if (desc) {
    const d = document.createElement('p');
    d.className = 'tile-desc';
    d.textContent = desc;
    body.appendChild(d);
  }

  const contributes = item.manifest?.contributes ?? [];
  const showWidgetChip = entryKind(item.entry) === 'dashboard-widget';
  if (contributes.length > 0 || showWidgetChip) {
    const wrap = document.createElement('div');
    wrap.className = 'tile-chips';
    if (showWidgetChip) {
      // Storefront cue: only widgets get a kind chip on the tile; viewer-plugins
      // are the default and chip-spam would clutter the row.
      const chip = document.createElement('span');
      chip.className = 'chip mono badge-kind-widget';
      chip.textContent = 'widget';
      wrap.appendChild(chip);
    }
    for (const c of contributes.slice(0, 4)) {
      const chip = document.createElement('span');
      chip.className = 'chip mono';
      chip.textContent = c;
      wrap.appendChild(chip);
    }
    body.appendChild(wrap);
  }

  if (item.manifestError) {
    const err = document.createElement('div');
    err.className = 'tile-error-badge';
    err.textContent = 'Manifest error';
    err.title = item.manifestError;
    body.appendChild(err);
  }

  tile.appendChild(body);
  return tile;
}

/**
 * Build a gradient placeholder for tiles without a screenshot. Hue is hashed
 * from the plugin id so the same id always yields the same gradient.
 */
function buildTilePlaceholder(item: ListedPlugin): HTMLDivElement {
  const ph = document.createElement('div');
  ph.className = 'tile-media-placeholder';
  const id = item.entry.id;
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  const hue1 = ((hash % 360) + 360) % 360;
  const hue2 = (hue1 + 40) % 360;
  ph.style.backgroundImage = `linear-gradient(135deg, hsl(${hue1} 48% 30%), hsl(${hue2} 42% 22%))`;
  const initial = (item.entry.label || item.entry.id).trim().charAt(0).toUpperCase() || '?';
  const letter = document.createElement('span');
  letter.className = 'tile-media-letter';
  letter.textContent = initial;
  ph.appendChild(letter);
  return ph;
}

/** Open the detail modal: hero, specs, and the admin actions. */
function openPluginDetail(item: ListedPlugin, filtered: ListedPlugin[]): void {
  // Mutable indirection: openDetailModal invokes renderBody synchronously, so
  // `const handle = openDetailModal({ renderBody: h => fn(h, handle) })` hits
  // the TDZ.
  const ref: { handle: DetailModalHandle | null } = { handle: null };
  ref.handle = openDetailModal({
    onClose: () => {},
    // A LAZY closer, not `ref.handle!`: renderBody runs synchronously inside
    // openDetailModal, when ref.handle is still null. Reading it at call time is
    // what stops Save/Delete throwing AFTER succeeding.
    renderBody: (host) => renderDetailBody(host, item, { close: () => ref.handle?.close() }, filtered),
  });
}

/** Only `close` is used, and it must be read LATE — see openPluginDetail. */
type ModalCloser = { close: () => void };

function renderDetailBody(
  host: HTMLDivElement,
  item: ListedPlugin,
  handle: ModalCloser,
  filtered: ListedPlugin[],
): void {
  host.innerHTML = '';
  host.className = 'detail-modal-body';

  // Hero
  const hero = document.createElement('div');
  hero.className = 'detail-hero';
  const shotUrl = screenshotUrl(item);
  if (shotUrl) {
    const img = document.createElement('img');
    img.src = shotUrl;
    img.alt = `${item.manifest?.label ?? item.entry.label ?? item.entry.id} screenshot`;
    img.onclick = () => openLightbox(shotUrl, img.alt);
    img.style.cursor = 'zoom-in';
    img.onerror = () => {
      img.remove();
      hero.appendChild(buildTilePlaceholder(item));
    };
    hero.appendChild(img);
  } else {
    hero.appendChild(buildTilePlaceholder(item));
  }
  host.appendChild(hero);

  // Title block
  const titleBlock = document.createElement('div');
  titleBlock.className = 'detail-title';
  const h = document.createElement('h2');
  h.textContent = item.entry.label || item.entry.id;
  titleBlock.appendChild(h);
  const meta = document.createElement('div');
  meta.className = 'detail-title-meta';
  const idB = document.createElement('span');
  idB.className = 'mono badge';
  idB.textContent = item.entry.id;
  const verB = document.createElement('span');
  verB.className = 'mono badge badge-accent';
  verB.textContent = `v${item.entry.version}`;
  meta.append(idB, verB);
  const author = item.entry.author ?? item.manifest?.author;
  if (author) {
    const a = document.createElement('span');
    a.className = 'detail-author';
    a.textContent = `by ${author}`;
    meta.appendChild(a);
  }
  titleBlock.appendChild(meta);
  host.appendChild(titleBlock);

  // Description (full, no clamp)
  const desc = item.entry.description ?? item.manifest?.description;
  if (desc) {
    const p = document.createElement('p');
    p.className = 'detail-desc';
    p.textContent = desc;
    host.appendChild(p);
  }

  // Spec list
  if (item.manifestError) {
    const errBlock = document.createElement('div');
    errBlock.className = 'card-error';
    errBlock.innerHTML = `
      <span class="card-error-icon" aria-hidden="true">!</span>
      <span>Manifest fetch failed: ${escapeHtml(item.manifestError)}</span>
    `;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'ghost-btn';
    retry.textContent = 'Retry';
    retry.onclick = async () => {
      retry.disabled = true;
      retry.textContent = 'Retrying…';
      try {
        const manifest = await fetchManifest(absoluteUrl(item.entry.manifestUrl));
        item.manifest = manifest;
        item.manifestError = undefined;
        renderDetailBody(host, item, handle, filtered);
        await loadAndRender();
      } catch (e) {
        retry.disabled = false;
        retry.textContent = 'Retry';
        item.manifestError = (e as Error).message;
        renderDetailBody(host, item, handle, filtered);
      }
    };
    errBlock.appendChild(retry);
    host.appendChild(errBlock);
  } else if (item.manifest) {
    host.appendChild(renderDetailSpecs(item));
  }

  // Admin row
  const admin = document.createElement('div');
  admin.className = 'detail-admin';
  const adminKicker = document.createElement('p');
  adminKicker.className = 'section-kicker';
  adminKicker.textContent = 'Admin';
  admin.appendChild(adminKicker);

  const adminActions = document.createElement('div');
  adminActions.className = 'detail-admin-actions';

  // The one action: install THIS version in the editor with its declared
  // sections applied. The cfg gets no button of its own — its contents are the
  // section checkboxes in the edit form, and a missing one is harmless (the
  // editor logs the miss and opens plain).
  //
  // VIEWER PLUGINS ONLY. The editor is a Viewer host; it cannot run a dashboard
  // widget, and offering the link anyway just sends the reviewer to an editor
  // that refuses to install what they clicked. Widgets are reviewed in the
  // dashboard, which has no install deep-link yet.
  const manifestHref = absoluteUrl(item.entry.manifestUrl);
  const reviewable = entryKind(item.entry) === 'viewer-plugin';
  const openApp = document.createElement('a');
  if (reviewable) {
    openApp.className = 'ghost-link';
    openApp.href = editorUrl(
      EDITOR_BASE.replace(/admin\/$/, ''),
      manifestHref.replace(/manifest\.json$/, 'app-cfg.json'),
      manifestHref,
    );
    openApp.target = '_blank';
    openApp.rel = 'noopener';
    openApp.textContent = 'Open in editor';
    openApp.title = 'Open the editor with this version installed and its declared sections applied.';
  }

  const editBtn = document.createElement('button');
  editBtn.type = 'button';
  editBtn.className = 'ghost-btn';
  editBtn.textContent = 'Edit';
  editBtn.disabled = !item.manifest;
  editBtn.onclick = () => beginDetailEdit(host, item, handle, filtered);

  const delBtn = document.createElement('button');
  delBtn.type = 'button';
  delBtn.className = 'danger-btn';
  delBtn.textContent = 'Delete';
  delBtn.onclick = async () => {
    const ok = await confirmDelete(item);
    if (ok) handle.close();
  };

  if (reviewable) adminActions.append(openApp);
  adminActions.append(editBtn, delBtn);
  admin.appendChild(adminActions);
  host.appendChild(admin);
}

function renderDetailSpecs(item: ListedPlugin): HTMLDListElement {
  const m = item.manifest!;
  const dl = document.createElement('dl');
  dl.className = 'detail-spec';

  const engineLines = Object.entries(m.engines ?? {});
  if (engineLines.length) {
    const wrap = document.createElement('span');
    wrap.className = 'engine-list';
    for (const [name, range] of engineLines) {
      const row = document.createElement('div');
      const n = document.createElement('span');
      n.className = 'mono';
      n.textContent = name;
      const r = document.createElement('span');
      r.className = 'mono';
      r.textContent = ` ${range}`;
      row.append(n, r);
      wrap.appendChild(row);
    }
    appendMetaRow(dl, 'Engines', wrap);
  }

  // The shelf it sits on. Unclassified is a real state for entries published
  // before `type` existed, so it is shown as such rather than hidden.
  const type = item.entry.type ?? m.type;
  appendMetaRow(dl, 'Type', type ? chips([type]) : emdash());

  const contrib = m.contributes ?? [];
  appendMetaRow(dl, 'Contributes', contrib.length ? chips(contrib) : emdash());

  const bundleUrl = absoluteUrl(m.entryUrl, item.entry.manifestUrl);
  appendMetaRow(dl, 'Bundle', mono(bundleUrl));
  appendMetaRow(dl, 'Manifest', mono(absoluteUrl(item.entry.manifestUrl)));

  // Raw JSON disclosure for debugging.
  const details = document.createElement('details');
  details.className = 'raw-json';
  const summary = document.createElement('summary');
  summary.textContent = 'View raw JSON';
  const pre = document.createElement('pre');
  pre.className = 'mono';
  pre.textContent = JSON.stringify(m, null, 2);
  details.append(summary, pre);
  const rowDt = document.createElement('dt');
  rowDt.textContent = 'JSON';
  const rowDd = document.createElement('dd');
  rowDd.appendChild(details);
  dl.append(rowDt, rowDd);

  return dl;
}

/** What the edit form collects beyond the plain text fields. */
interface EditExtras {
  type: PluginType | '';
  sections: string[];
  dependsOn: string[];
}

/** The Type choices a kind allows — a widget's is fixed, a viewer plugin's is a judgement. */
const TYPE_OPTIONS: Record<PluginKind, Array<[PluginType | '', string]>> = {
  'viewer-plugin': [
    ['', '— unclassified —'], ['app', 'app (opens as a destination)'], ['tool', 'tool (augments an open scene)'],
  ],
  'dashboard-widget': [['dashboard', 'dashboard (a cell on the supervision board)']],
};

/**
 * The non-free-text half of the manifest: what the plugin is, the surface it
 * needs, what it needs alongside it. Editable here because the store's copy is
 * authoritative once published — a reviewer fixing a key shouldn't need a
 * release. Checklists, not text boxes: the keys are a closed set.
 */
function buildAdvancedEditor(
  m: PluginManifest,
  others: ListedPlugin[],
): { el: HTMLElement; read: () => EditExtras; setKind: (kind: PluginKind) => void } {
  const wrap = document.createElement('div');
  wrap.className = 'edit-advanced';

  const fillSelect = (sel: HTMLSelectElement, value: string, opts: Array<[string, string]>): void => {
    sel.innerHTML = '';
    for (const [v, t] of opts) {
      const o = document.createElement('option');
      o.value = v;
      o.textContent = t;
      if (v === value) o.selected = true;
      sel.appendChild(o);
    }
  };
  const mkSelect = (label: string, value: string, opts: Array<[string, string]>): HTMLSelectElement => {
    const lbl = document.createElement('label');
    lbl.textContent = label;
    const sel = document.createElement('select');
    fillSelect(sel, value, opts);
    wrap.append(lbl, sel);
    return sel;
  };

  // No Category select: `kind` above already says 3-D vs 2-D. The Type choices
  // FOLLOW the kind: a widget is `dashboard` and nothing else (shown, not
  // asked), a viewer plugin is an app or a tool by the author's judgement.
  const typeSel = mkSelect('Type', m.type ?? '', TYPE_OPTIONS[m.kind ?? 'viewer-plugin']);
  typeSel.disabled = TYPE_OPTIONS[m.kind ?? 'viewer-plugin'].length === 1;
  // The app/tool judgement survives a round trip through the widget kind —
  // a mis-click on Kind should not cost the author their classification.
  let viewerChoice: string = m.type === 'dashboard' ? '' : (m.type ?? '');
  const setKind = (kind: PluginKind): void => {
    if (!typeSel.disabled) viewerChoice = typeSel.value;
    const opts = TYPE_OPTIONS[kind];
    fillSelect(typeSel, kind === 'viewer-plugin' ? viewerChoice : opts[0][0], opts);
    typeSel.disabled = opts.length === 1;
  };

  // An empty group means "not declared", which hosts read as no opinion.
  const mkChecks = (label: string, hint: string, values: string[], checked: string[]) => {
    const lbl = document.createElement('label');
    lbl.textContent = label;
    const box = document.createElement('div');
    box.className = 'edit-checks';
    const inputs: HTMLInputElement[] = [];
    for (const v of values) {
      const l = document.createElement('label');
      l.className = 'edit-check';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.value = v;
      cb.checked = checked.includes(v);
      l.append(cb, document.createTextNode(` ${v}`));
      box.appendChild(l);
      inputs.push(cb);
    }
    const help = document.createElement('p');
    help.className = 'muted';
    help.textContent = hint;
    wrap.append(lbl, box, help);
    return () => inputs.filter((i) => i.checked).map((i) => i.value);
  };

  // SYSTEM sections only — another plugin's panel is dependsOn's job, not a
  // surface this one can request. A declared key no longer offered still shows,
  // checked, so an edit never silently drops it.
  const sectionOptions = [...BUILTIN_SECTIONS];
  const declared = m.ui?.sidebar?.sections ?? [];
  for (const k of declared) if (!sectionOptions.includes(k)) sectionOptions.push(k);
  const readSections = mkChecks(
    'UI sections this plugin needs',
    'Built-in sidebar sections. It is a REQUEST: a host composes it with its own policy and entitlement, and restriction wins. '
    + "This plugin's own panel is always shown — it does not need declaring.",
    sectionOptions,
    declared,
  );

  // LINKAGE — TOOLS only. A dependency is something that augments the scene this
  // plugin works in; an app owns the screen, so depending on one would mean two
  // things claiming it. No toggle: an option nobody should pick is not an option.
  const depLbl = document.createElement('label');
  depLbl.textContent = 'Depends on (tools)';
  const depSel = document.createElement('select');
  depSel.multiple = true;
  depSel.size = 6;
  const paintDeps = () => {
    const current = new Set([...depSel.selectedOptions].map((o) => o.value));
    for (const v of m.dependsOn ?? []) current.add(v);
    depSel.innerHTML = '';
    const pool = others.filter((o) => {
      const t = o.entry.type ?? o.manifest?.type;
      // Unclassified entries stay visible — everything published before `type`
      // existed has none, and hiding them would look like they vanished. A
      // dashboard widget is no more a scene augmentation than an app is.
      return t !== 'app' && t !== 'dashboard';
    });
    for (const o of pool) {
      const opt = document.createElement('option');
      opt.value = o.entry.id;
      const t = o.entry.type ?? o.manifest?.type ?? 'unclassified';
      opt.textContent = `${o.entry.label} · ${o.entry.id} (${t})`;
      opt.selected = current.has(o.entry.id);
      depSel.appendChild(opt);
    }
    // A dependency on something not in this registry is still a real
    // dependency; keep it selectable rather than losing it on save.
    for (const id of current) {
      if (!pool.some((o) => o.entry.id === id)) {
        const opt = document.createElement('option');
        opt.value = id;
        opt.textContent = `${id} (not in this store)`;
        opt.selected = true;
        depSel.appendChild(opt);
      }
    }
  };
  paintDeps();
  const depHelp = document.createElement('p');
  depHelp.className = 'muted';
  depHelp.textContent = 'Declarative: the host does not auto-install these, so the plugin must still degrade honestly without them.';
  // One value, one grid cell. Appended as siblings these auto-place into
  // successive cells, which drops the list into the label column — and that
  // column is sized to its contents, so a long option would stretch it and shove
  // every field right.
  const depCell = document.createElement('div');
  depCell.className = 'edit-dep';
  depCell.append(depSel);
  wrap.append(depLbl, depCell, depHelp);

  return {
    el: wrap,
    read: () => ({
      type: typeSel.value as EditExtras['type'],
      sections: readSections(),
      dependsOn: [...depSel.selectedOptions].map((o) => o.value),
    }),
    setKind,
  };
}

/** Replace the detail body with the edit form. */
function beginDetailEdit(
  host: HTMLDivElement,
  item: ListedPlugin,
  handle: ModalCloser,
  filtered: ListedPlugin[],
): void {
  if (!item.manifest) return;
  host.innerHTML = '';
  host.className = 'detail-modal-body editing';

  const heading = document.createElement('h2');
  heading.textContent = `Edit ${item.entry.label}`;
  host.appendChild(heading);

  const m = item.manifest;
  const grid = document.createElement('div');
  grid.className = 'edit-grid';
  const fields: Array<[string, string, string, string]> = [
    ['label', 'Label', m.label, 'text'],
    ['version', 'Version', m.version, 'text'],
    ['author', 'Author', m.author ?? '', 'text'],
    ['description', 'Description', m.description ?? '', 'textarea'],
    ['kind', 'Kind', m.kind ?? 'viewer-plugin', 'select-kind'],
  ];
  for (const [key, labelText, value, kind] of fields) {
    const lbl = document.createElement('label');
    lbl.textContent = labelText;
    lbl.htmlFor = `edit-${item.entry.id}-${key}`;
    let input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (kind === 'textarea') {
      input = document.createElement('textarea');
    } else if (kind === 'select-kind') {
      input = document.createElement('select');
      for (const opt of [
        { v: 'viewer-plugin', t: 'Viewer plugin' },
        { v: 'dashboard-widget', t: 'Dashboard widget' },
      ]) {
        const o = document.createElement('option');
        o.value = opt.v;
        o.textContent = opt.t;
        input.appendChild(o);
      }
    } else {
      input = document.createElement('input');
      (input as HTMLInputElement).type = kind;
    }
    input.id = `edit-${item.entry.id}-${key}`;
    input.dataset.field = key;
    input.value = value;
    grid.append(lbl, input);
  }
  host.appendChild(grid);

  // Classification / declared UI / linkage — the fields the publish path
  // validates but nothing could previously EDIT, which meant a mis-declared
  // section could only be fixed by the author cutting a new version.
  const advanced = buildAdvancedEditor(m, lastItems.filter((p) => p.entry.id !== item.entry.id));
  host.appendChild(advanced.el);
  // Kind and Type are one decision seen from two sides: flipping the kind
  // re-offers the types that kind allows, so the form can't be saved in a
  // state the store rules would then refuse.
  grid.querySelector<HTMLSelectElement>('select[data-field="kind"]')?.addEventListener('change', (ev) => {
    advanced.setKind((ev.target as HTMLSelectElement).value === 'dashboard-widget' ? 'dashboard-widget' : 'viewer-plugin');
  });

  const editActions = document.createElement('div');
  editActions.className = 'detail-admin-actions';
  editActions.style.justifyContent = 'flex-end';
  editActions.style.marginTop = '1rem';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.textContent = 'Cancel';
  cancel.onclick = () => renderDetailBody(host, item, handle, filtered);

  const save = document.createElement('button');
  save.type = 'button';
  save.className = 'primary-btn';
  save.textContent = 'Save';
  save.onclick = async () => {
    save.disabled = true;
    cancel.disabled = true;
    save.textContent = 'Saving…';
    try {
      const updates: Record<string, string> = {};
      grid.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[data-field]').forEach((el) => {
        updates[el.dataset.field!] = el.value.trim();
      });
      await saveEdit(item, updates, advanced.read());
      await loadAndRender();
      handle.close();
      toast('success', `Saved ${item.entry.id}.`);
    } catch (e) {
      save.disabled = false;
      cancel.disabled = false;
      save.textContent = 'Save';
      toast('error', `Save failed: ${(e as Error).message}`);
    }
  };

  editActions.append(cancel, save);
  host.appendChild(editActions);
}

function appendMetaRow(dl: HTMLDListElement, key: string, valueNode: Node): void {
  const dt = document.createElement('dt');
  dt.textContent = key;
  const dd = document.createElement('dd');
  dd.appendChild(valueNode);
  dl.append(dt, dd);
}

function text(s: string): Node {
  return document.createTextNode(s);
}
function mono(s: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'mono';
  span.textContent = s;
  return span;
}
function emdash(): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'chip chip-muted';
  span.textContent = '—';
  return span;
}
function chips(items: readonly string[]): HTMLSpanElement {
  const wrap = document.createElement('span');
  wrap.className = 'chips';
  for (const it of items) {
    const c = document.createElement('span');
    c.className = 'chip mono';
    c.textContent = it;
    wrap.appendChild(c);
  }
  return wrap;
}

// --- Edit --------------------------------------------------------------------

async function saveEdit(
  item: ListedPlugin,
  updates: Record<string, string>,
  extras?: EditExtras,
): Promise<void> {
  if (!item.manifest) throw new Error('manifest missing');
  if (!updates.label) throw new Error('label is required');
  if (!updates.version || !/^\d+\.\d+\.\d+/.test(updates.version)) {
    throw new Error('version must be semver like 0.1.0');
  }
  const versionChanged = updates.version !== item.manifest.version;
  const newKind: PluginKind = updates.kind === 'dashboard-widget' ? 'dashboard-widget' : 'viewer-plugin';

  const newManifest: PluginManifest = {
    ...item.manifest,
    label: updates.label,
    version: updates.version,
    author: updates.author || undefined,
    description: updates.description || undefined,
    kind: newKind,
    ...(extras
      ? {
        type: extras.type || undefined,
        dependsOn: extras.dependsOn.length ? extras.dependsOn : undefined,
        // Preserve any other `ui` keys (splitView, infobar…) the author set;
        // this editor only owns the sidebar section list.
        ui: extras.sections.length
          ? { ...(item.manifest.ui ?? {}), sidebar: { ...(item.manifest.ui?.sidebar ?? {}), sections: extras.sections } }
          : undefined,
      }
      : {}),
  };
  // Same rules as publish, minus immutability: editing in place is the point of
  // a store-owned copy. Before any PUT. An edit with no advanced form (or a
  // widget that predates the type) still leaves as `dashboard`.
  stampType(newManifest);
  checkStoreRules(newManifest, undefined);

  if (versionChanged) {
    // Move bundle + screenshots into the new version dir, then drop the old.
    const oldBase = `${PLUGINS_BASE}/${item.entry.id}/${item.manifest.version}`;
    const newBase = `${PLUGINS_BASE}/${item.entry.id}/${updates.version}`;
    // Copy the bundle.
    const bundleResp = await fetch(`${oldBase}/${item.manifest.entryUrl}`, { cache: 'no-store' });
    if (!bundleResp.ok) throw new Error(`bundle fetch HTTP ${bundleResp.status}`);
    const bundleBody = await bundleResp.blob();
    await putFile(`${newBase}/${item.manifest.entryUrl}`, bundleBody, 'text/javascript');
    // Copy screenshots, if any.
    for (const shot of item.manifest.screenshots ?? []) {
      const r = await fetch(`${oldBase}/${shot}`, { cache: 'no-store' });
      if (r.ok) {
        const blob = await r.blob();
        await putFile(`${newBase}/${shot}`, blob, blob.type || 'application/octet-stream');
      }
    }
    // Write the new manifest.
    await putFile(
      `${newBase}/manifest.json`,
      JSON.stringify(newManifest, null, 2),
      'application/json',
    );
    // Re-derived, not copied — without this the new version had no cfg at all.
    await putFile(`${newBase}/app-cfg.json`, deriveAppCfg(newManifest), 'application/json');
    // Remove old directory's files. Best-effort.
    await deleteFile(`${oldBase}/manifest.json`);
    await deleteFile(`${oldBase}/${item.manifest.entryUrl}`);
    for (const shot of item.manifest.screenshots ?? []) {
      await deleteFile(`${oldBase}/${shot}`);
    }
  } else {
    // Same version → overwrite manifest in place.
    const base = `${PLUGINS_BASE}/${item.entry.id}/${updates.version}`;
    await putFile(`${base}/manifest.json`, JSON.stringify(newManifest, null, 2), 'application/json');
    // Or the store would show new sections while every host kept the old.
    await putFile(`${base}/app-cfg.json`, deriveAppCfg(newManifest), 'application/json');
  }

  // Update registry entry.
  const reg = await fetchRegistry();
  const idx = reg.plugins.findIndex((p) => p.id === item.entry.id);
  const updatedEntry: PluginRegistryEntry = {
    id: newManifest.id,
    kind: newKind,
    version: newManifest.version,
    label: newManifest.label,
    description: newManifest.description,
    author: newManifest.author,
    manifestUrl: `${PLUGINS_BASE}/${newManifest.id}/${newManifest.version}/manifest.json`,
    ...(newManifest.type ? { type: newManifest.type } : {}),
  };
  if (idx >= 0) reg.plugins[idx] = updatedEntry;
  else reg.plugins.push(updatedEntry);
  await writeRegistry(reg);
}

// --- Delete ------------------------------------------------------------------

async function confirmDelete(item: ListedPlugin): Promise<boolean> {
  const ok = await confirmModal({
    title: `Delete "${item.entry.label}"?`,
    body: `Removes ${item.entry.id} v${item.entry.version} from /plugins/ and drops it from the registry.`,
    confirmLabel: 'Delete',
    cancelLabel: 'Cancel',
    danger: true,
  });
  if (!ok) return false;
  try {
    if (item.manifest) {
      const base = `${PLUGINS_BASE}/${item.entry.id}/${item.manifest.version}`;
      await deleteFile(`${base}/${item.manifest.entryUrl}`);
      await deleteFile(`${base}/manifest.json`);
      for (const shot of item.manifest.screenshots ?? []) {
        await deleteFile(`${base}/${shot}`);
      }
    } else {
      // No manifest → still try to remove the manifest file we know about.
      await deleteFile(absoluteUrl(item.entry.manifestUrl));
    }
    const reg = await fetchRegistry();
    reg.plugins = reg.plugins.filter((p) => p.id !== item.entry.id);
    await writeRegistry(reg);
    toast('success', `Deleted ${item.entry.id}.`);
    await loadAndRender();
    return true;
  } catch (e) {
    toast('error', `Delete failed: ${(e as Error).message}`);
    return false;
  }
}

// --- Upload ------------------------------------------------------------------

// Wire each drop zone: drag-drop + click-to-open + clear button. Hidden native
// input remains the source of truth for the file value.
function setupDropZones(): void {
  document.querySelectorAll<HTMLDivElement>('.drop').forEach((zone) => {
    const inputId = zone.dataset.input!;
    const input = document.getElementById(inputId) as HTMLInputElement;
    const fileLabel = zone.querySelector<HTMLDivElement>('.drop-file')!;
    const clearBtn = zone.querySelector<HTMLButtonElement>('.drop-clear')!;

    const update = (): void => {
      const f = input.files?.[0];
      if (f) {
        zone.classList.add('has-file');
        fileLabel.textContent = `${f.name} · ${formatBytes(f.size)}`;
        clearBtn.hidden = false;
      } else {
        zone.classList.remove('has-file');
        fileLabel.textContent = '';
        clearBtn.hidden = true;
      }
    };

    zone.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.drop-clear')) return;
      input.click();
    });
    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.classList.add('dragover');
    });
    zone.addEventListener('dragleave', () => zone.classList.remove('dragover'));
    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('dragover');
      const file = e.dataTransfer?.files?.[0];
      if (!file) return;
      // Assign via DataTransfer so the input.files reflects the drop.
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('change', update);
    clearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      input.value = '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

setupDropZones();

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

$manifestInput.addEventListener('change', async () => {
  pendingManifest = null;
  $publishBtn.disabled = true;
  const file = $manifestInput.files?.[0];
  if (!file) {
    renderManifestPreviewEmpty();
    return;
  }
  try {
    const text = await file.text();
    const parsed = validateManifest(JSON.parse(text));
    pendingManifest = parsed;
    // If the manifest declares a kind, mirror it on the selector so the user
    // doesn't have to set it twice. They can still override before publish.
    if ($kindSelect && parsed.kind) {
      $kindSelect.value = parsed.kind === 'dashboard-widget' ? 'dashboard-widget' : 'viewer-plugin';
    }
    renderManifestSummary(parsed);
    updatePublishEnabled();
  } catch (e) {
    renderManifestError((e as Error).message);
  }
});

$bundleInput.addEventListener('change', updatePublishEnabled);

$screenshotInput.addEventListener('change', () => {
  const preview = document.getElementById('mp-screenshot-preview') as HTMLDivElement;
  preview.innerHTML = '';
  const file = $screenshotInput.files?.[0];
  if (!file) {
    preview.hidden = true;
    return;
  }
  const img = document.createElement('img');
  img.src = URL.createObjectURL(file);
  img.alt = 'Screenshot preview';
  img.onload = () => URL.revokeObjectURL(img.src);
  preview.appendChild(img);
  preview.hidden = false;
});

function renderManifestPreviewEmpty(): void {
  $manifestPreview.classList.remove('error', 'valid');
  $manifestPreview.innerHTML = '<div class="muted preview-empty">Pick a manifest to see parsed metadata.</div>';
  $publishBtn.title = '';
}

function renderManifestError(msg: string): void {
  $manifestPreview.classList.remove('valid');
  $manifestPreview.classList.add('error');
  $manifestPreview.innerHTML = `
    <div class="preview-error">
      <span class="card-error-icon" aria-hidden="true">!</span>
      <div>
        <div class="preview-error-title">Invalid manifest</div>
        <div class="preview-error-body">${escapeHtml(msg)}</div>
      </div>
    </div>
  `;
  $publishBtn.title = 'Manifest is invalid — fix and reselect';
}

function renderManifestSummary(m: PluginManifest): void {
  $manifestPreview.classList.remove('error');
  $manifestPreview.classList.add('valid');
  $publishBtn.title = '';

  const card = document.createElement('div');
  card.className = 'preview-summary';

  const head = document.createElement('div');
  head.className = 'preview-head';
  const title = document.createElement('div');
  title.className = 'preview-title';
  title.textContent = m.label;
  const badges = document.createElement('div');
  badges.className = 'preview-badges';
  const idB = document.createElement('span');
  idB.className = 'mono badge';
  idB.textContent = m.id;
  const verB = document.createElement('span');
  verB.className = 'mono badge badge-accent';
  verB.textContent = `v${m.version}`;
  const kindB = document.createElement('span');
  const k: PluginKind = m.kind ?? 'viewer-plugin';
  kindB.className = k === 'dashboard-widget' ? 'mono badge badge-kind-widget' : 'mono badge';
  kindB.textContent = k === 'dashboard-widget' ? 'dashboard-widget' : 'viewer-plugin';
  badges.append(idB, verB, kindB);
  head.append(title, badges);
  card.appendChild(head);

  const dl = document.createElement('dl');
  dl.className = 'plugin-meta preview-meta';

  if (m.author) appendMetaRow(dl, 'Author', text(m.author));
  if (m.description) appendMetaRow(dl, 'Description', text(m.description));

  const engineLines = Object.entries(m.engines ?? {});
  if (engineLines.length) {
    const wrap = document.createElement('span');
    wrap.className = 'engine-list';
    for (const [name, range] of engineLines) {
      const row = document.createElement('div');
      const n = document.createElement('span');
      n.className = 'mono';
      n.textContent = name;
      const sep = document.createTextNode(' ');
      const r = document.createElement('span');
      r.className = 'mono';
      r.textContent = range;
      row.append(n, sep, r);
      wrap.appendChild(row);
    }
    appendMetaRow(dl, 'Engines', wrap);
  }

  const contrib = m.contributes ?? [];
  if (contrib.length) appendMetaRow(dl, 'Contributes', chips(contrib));

  appendMetaRow(dl, 'Entry', mono(m.entryUrl));

  card.appendChild(dl);

  // "View raw JSON" disclosure for debugging.
  const details = document.createElement('details');
  details.className = 'raw-json';
  const summary = document.createElement('summary');
  summary.textContent = 'View raw JSON';
  const pre = document.createElement('pre');
  pre.className = 'mono';
  pre.textContent = JSON.stringify(m, null, 2);
  details.append(summary, pre);
  card.appendChild(details);

  $manifestPreview.innerHTML = '';
  $manifestPreview.appendChild(card);
}

function updatePublishEnabled(): void {
  $publishBtn.disabled = !(pendingManifest && $bundleInput.files?.[0]);
}

$form.addEventListener('submit', async (e) => {
  e.preventDefault();
  if (!pendingManifest) return;
  const bundleFile = $bundleInput.files?.[0];
  if (!bundleFile) return;
  const screenshotFile = $screenshotInput.files?.[0] ?? null;

  $publishBtn.disabled = true;
  $publishBtn.classList.add('busy');

  try {
    const m = pendingManifest;
    // The kind selector is authoritative at submit: it either confirms the
    // manifest's declared kind or overrides it.
    const selectedKind: PluginKind = $kindSelect?.value === 'dashboard-widget' ? 'dashboard-widget' : 'viewer-plugin';
    m.kind = selectedKind;
    stampType(m);
    const base = `${PLUGINS_BASE}/${m.id}/${m.version}`;
    // Store rules BEFORE the first PUT — see checkStoreRules.
    const regBefore = await fetchRegistry();
    checkStoreRules(m, regBefore.plugins.find((p) => p.id === m.id));
    // Bundle: keep the manifest's entryUrl filename (relative). If absolute or
    // contains slashes, normalise to "index.js" to keep paths predictable.
    const bundleName = m.entryUrl.includes('/') || /^https?:/i.test(m.entryUrl) ? 'index.js' : m.entryUrl;
    if (bundleName !== m.entryUrl) m.entryUrl = bundleName;
    await putFile(`${base}/${bundleName}`, await bundleFile.arrayBuffer(), 'text/javascript');

    if (screenshotFile) {
      const ext = screenshotFile.name.match(/\.(png|jpe?g|webp)$/i)?.[0] ?? '.png';
      const shotName = `screenshot${ext.toLowerCase()}`;
      await putFile(`${base}/${shotName}`, await screenshotFile.arrayBuffer(), screenshotFile.type || 'image/png');
      m.screenshots = [shotName];
    }

    await putFile(`${base}/manifest.json`, JSON.stringify(m, null, 2), 'application/json');
    // Beside the manifest, so every host fetches the same one.
    await putFile(`${base}/app-cfg.json`, deriveAppCfg(m), 'application/json');

    // Re-read for the WRITE: a slow upload would make regBefore stale and drop
    // anything published meanwhile.
    const reg = await fetchRegistry();
    const idx = reg.plugins.findIndex((p) => p.id === m.id);
    const entry: PluginRegistryEntry = {
      id: m.id,
      kind: selectedKind,
      version: m.version,
      label: m.label,
      description: m.description,
      author: m.author,
      manifestUrl: `${base}/manifest.json`,
      // Mirrored so the index is filterable without fetching every manifest.
      // Absent must read as "show it" — older entries have none.
      ...(m.type ? { type: m.type } : {}),
    };
    if (idx >= 0) reg.plugins[idx] = entry;
    else reg.plugins.push(entry);
    await writeRegistry(reg);

    toast('success', `Published ${m.id} v${m.version}.`);
    resetForm();
    await loadAndRender();
  } catch (err) {
    const msg = (err as Error).message;
    toast('error', `Publish failed: ${msg}`);
  } finally {
    $publishBtn.disabled = false;
    $publishBtn.classList.remove('busy');
    updatePublishEnabled();
  }
});

function resetForm(): void {
  $form.reset();
  pendingManifest = null;
  if ($kindSelect) $kindSelect.value = 'viewer-plugin';
  renderManifestPreviewEmpty();
  // Sync drop-zone visuals after .reset() (which clears inputs without
  // firing change events on all browsers).
  document.querySelectorAll<HTMLDivElement>('.drop').forEach((zone) => {
    const id = zone.dataset.input!;
    const input = document.getElementById(id) as HTMLInputElement;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  const preview = document.getElementById('mp-screenshot-preview') as HTMLDivElement;
  preview.innerHTML = '';
  preview.hidden = true;
}

// --- Misc --------------------------------------------------------------------

$refresh.addEventListener('click', () => void loadAndRender());

if ($filter) {
  $filter.addEventListener('input', () => applyFilterAndRender());
}

function setKindTab(kind: 'viewer-plugin' | 'dashboard-widget'): void {
  if (currentKindTab === kind) return;
  currentKindTab = kind;
  if ($tabViewer) {
    const on = kind === 'viewer-plugin';
    $tabViewer.classList.toggle('active', on);
    $tabViewer.setAttribute('aria-selected', String(on));
  }
  if ($tabWidget) {
    const on = kind === 'dashboard-widget';
    $tabWidget.classList.toggle('active', on);
    $tabWidget.setAttribute('aria-selected', String(on));
  }
  applyFilterAndRender();
}

$tabViewer?.addEventListener('click', () => setKindTab('viewer-plugin'));
$tabWidget?.addEventListener('click', () => setKindTab('dashboard-widget'));

// Sticky header gets a hairline border once the page scrolls (mirrors landing).
setupHeaderScroll();
function setupHeaderScroll(): void {
  const header = document.querySelector<HTMLElement>('.site-header');
  if (!header) return;
  const update = (): void => {
    header.classList.toggle('scrolled', window.scrollY > 4);
  };
  update();
  window.addEventListener('scroll', update, { passive: true });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/**
 * Resolve a registry URL — against a manifest when given (a bundle/screenshot
 * named relative to its own manifest), else by the store rule in
 * `resolveStoreUrl`. Always absolute: these are handed to the editor as query
 * params, and a bare path means something else at another page depth.
 */
function absoluteUrl(url: string, baseUrl?: string): string {
  if (baseUrl && !/^https?:\/\//i.test(url) && !url.startsWith('/')) {
    return new URL(url, new URL(absoluteUrl(baseUrl), window.location.href)).href;
  }
  return resolveStoreUrl(SITE, REGISTRY_URL, url, window.location.href);
}

void loadAndRender();
